import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import FormModeToggle from './FormModeToggle';
import IdeaFormFields, { IdeaFormData } from './IdeaFormFields';
import SimulationFormFields, { SimulationFormData } from './SimulationFormFields';
import FormPresets from './FormPresets';
import { glassContainer, glassButton, fadeInUp } from './glassMorphismStyles';
import { trackMicroConversion } from '../../utils/analytics';
import { safeLocalStorageGetJSON, safeLocalStorageRemoveItem } from '../../utils/storageHelper';

interface LandingInteractiveFormProps {
  onSubmit?: (mode: 'ideas' | 'simulation', data: any) => void;
}

const LandingInteractiveForm: React.FC<LandingInteractiveFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const { isAuthenticated, storeLandingFormData } = useAuth();
  const [mode, setMode] = useState<'ideas' | 'simulation'>('ideas');
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [publicIcps, setPublicIcps] = useState<any[]>([]);
  
  // Form data states
  const [ideaData, setIdeaData] = useState<IdeaFormData>({
    platform: 'Twitter/X',
    brand: '',
    brandDescription: '',
    sector: '',
    sectorId: '',
    topic: '',
    icpId: '',
  });
  
  const [simulationData, setSimulationData] = useState<SimulationFormData>({
    platform: 'Twitter/X',
    brand: '',
    brandDescription: '',
    sector: '',
    sectorId: '',
    content: '',
    imageFile: null,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch public ICPs on mount for preset mapping
  useEffect(() => {
    const fetchPublicICPs = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const response = await axios.get(`${API_URL}/api/v1/public/icps`);
        setPublicIcps(response.data.icps || []);
      } catch (error) {
        console.error('Error fetching public ICPs:', error);
      }
    };
    fetchPublicICPs();
    
    // Clean up old localStorage entries (older than 1 hour)
    const storedData = safeLocalStorageGetJSON<{ timestamp?: number } | null>('wavescoop_landing_form', null);
    if (storedData && storedData.timestamp) {
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      if (storedData.timestamp < oneHourAgo) {
        safeLocalStorageRemoveItem('wavescoop_landing_form');
      }
    }
  }, []);

  const handleModeChange = (newMode: 'ideas' | 'simulation') => {
    setMode(newMode);
    setErrors({});
    setSelectedPresetId('');

    // Track mode toggle as micro-conversion
    trackMicroConversion(
      newMode === 'ideas' ? 'landing_toggle_ideagen' : 'landing_toggle_postsim',
      1
    );
  };

  const handleIdeaFieldChange = (field: string, value: string) => {
    setIdeaData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Track form engagement micro-conversion
    if (field === 'brand' && value.length === 1) {
      trackMicroConversion('landing_form_engaged_ideagen', 1);
    }
  };

  const handleSimulationFieldChange = (field: string, value: any) => {
    setSimulationData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Track form engagement micro-conversion
    if (field === 'content' && value.length === 1) {
      trackMicroConversion('landing_form_engaged_postsim', 1);
    }
  };

  const handlePresetSelect = (presetData: any) => {
    if (mode === 'ideas') {
      // Map preset ICP title to actual ICP ID
      let icpId = '';
      if (presetData.preferredIcpTitle && publicIcps.length > 0) {
        const matchingIcp = publicIcps.find(icp => 
          icp.title.toLowerCase().includes(presetData.preferredIcpTitle.toLowerCase())
        );
        icpId = matchingIcp?.id || publicIcps[0]?.id || '';
      }
      
      setIdeaData({
        platform: presetData.platform || 'LinkedIn',
        brand: presetData.brand || '',
        brandDescription: presetData.brandDescription || '',
        sector: presetData.sector || '',
        sectorId: presetData.sectorId || '',
        topic: presetData.topic || '',
        icpId: icpId,
      });
    } else {
      setSimulationData({
        platform: presetData.platform || 'LinkedIn',
        brand: presetData.brand || '',
        brandDescription: presetData.brandDescription || '',
        sector: presetData.sector || '',
        sectorId: presetData.sectorId || '',
        content: presetData.content || '',
        imageFile: null,
      });
    }
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (mode === 'ideas') {
      if (!ideaData.platform) newErrors.platform = 'Platform is required';
      if (!ideaData.brand) newErrors.brand = 'Brand is required';
      if (!ideaData.sector) newErrors.sector = 'Sector is required';
      if (!ideaData.topic) newErrors.topic = 'Topic is required';
      if (!ideaData.icpId) newErrors.icpId = 'Persona is required';
    } else {
      if (!simulationData.platform) newErrors.platform = 'Platform is required';
      if (!simulationData.brand) newErrors.brand = 'Brand is required';
      if (!simulationData.sector) newErrors.sector = 'Sector is required';
      if (!simulationData.content) newErrors.content = 'Content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const data = mode === 'ideas' ? ideaData : simulationData;

    // Track form submission micro-conversion
    trackMicroConversion(
      mode === 'ideas' ? 'landing_form_submit_ideagen' : 'landing_form_submit_postsim',
      5
    );
    
    // For simulation mode, convert image to base64 if present
    let imageBase64 = null;
    let imageName = null;
    if (mode === 'simulation' && simulationData.imageFile) {
      try {
        const file = simulationData.imageFile;
        imageName = file.name;
        const reader = new FileReader();
        imageBase64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } catch (error) {
        console.error('Error converting image to base64:', error);
      }
    }
    
    // Save form data to sessionStorage for use after authentication
    const landingFormData = {
      mode,
      data: {
        ...data,
        imageBase64: imageBase64, // Store base64 instead of file
        imageFile: undefined, // Remove the file object as it can't be stored
        imageName: imageName, // Store the file name for reference
      },
      timestamp: Date.now(), // Add timestamp for cleanup
    };
    
    // Store in localStorage to persist through auth flow (including across tabs)
    storeLandingFormData(landingFormData);
    
    // If user is already authenticated, go directly to the appropriate creation page
    if (isAuthenticated) {
      if (mode === 'ideas') {
        navigate('/ideas/create', { state: { fromLanding: true, formData: data } });
      } else {
        navigate('/simulations/create', { state: { fromLanding: true, formData: landingFormData.data } });
      }
    } else {
      // Not authenticated, redirect to login with source parameter
      navigate(`/login?source=landing-form-${mode}`);
    }
    
    // Call the optional onSubmit callback if provided
    if (onSubmit) {
      onSubmit(mode, data);
    }
  };

  return (
    <Box
      sx={{
        ...glassContainer,
        p: { xs: 1.5, sm: 3, md: 3.5 },
        width: '100%',
        animation: `${fadeInUp} 0.8s ease-out`,
      }}
    >
      {/* Mode Toggle */}
      <FormModeToggle mode={mode} onChange={handleModeChange} />
      
      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          {mode === 'ideas' ? (
            <IdeaFormFields
              formData={ideaData}
              onChange={handleIdeaFieldChange}
              errors={errors}
            />
          ) : (
            <SimulationFormFields
              formData={simulationData}
              onChange={handleSimulationFieldChange}
              errors={errors}
            />
          )}
        </Box>
        
        {/* Presets */}
        <FormPresets
          mode={mode}
          onSelect={handlePresetSelect}
          selectedId={selectedPresetId}
        />
        
        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 1.5, sm: 3 } }}>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            sx={{
              ...glassButton,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.7, sm: 1 },
              fontSize: { xs: '0.85rem', md: '1rem' },
              borderRadius: '50px',
              minWidth: { xs: '140px', md: '180px' },
            }}
          >
            {mode === 'ideas' ? 'Generate Ideas' : 'Run Simulation'}
          </Button>
        </Box>
      </form>
      
      {/* Disclaimer */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          mt: { xs: 1, sm: 2 },
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: { xs: '0.68rem', md: '0.85rem' },
        }}
      >
        Free trial • No credit card required
      </Typography>
    </Box>
  );
};

export default LandingInteractiveForm;
